import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Completing sign in...");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Get the authorization code from URL params (Google OAuth)
        const code = searchParams.get("code");
        
        // Also check for legacy session_id in hash (Emergent OAuth)
        const hash = window.location.hash || "";
        const hashParams = new URLSearchParams(hash.substring(1));
        const sessionId = hashParams.get("session_id");

        if (!code && !sessionId) {
          setStatus("error");
          setMessage("No authorization found. Redirecting...");
          setTimeout(() => { window.location.href = "/"; }, 1500);
          return;
        }

        let response;
        
        if (code) {
          // The signup flow encodes the chosen user_type into OAuth `state`
          // so a brand-new account is created with the right role.
          const stateParam = searchParams.get("state") || "";
          let userType = null;
          if (stateParam.includes("worker")) userType = "support_worker";
          else if (stateParam.includes("participant")) userType = "participant";
          // Google OAuth - exchange code for token
          response = await fetch(`${API}/auth/google`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ 
              code,
              redirect_uri: window.location.origin + '/auth/callback',
              user_type: userType
            })
          });
        } else {
          // Legacy Emergent OAuth
          response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ session_id: sessionId })
          });
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || "Authentication failed");
        }

        const data = await response.json();

        // Store token and user data directly in localStorage
        if (data.token) {
          localStorage.setItem("auth_token", data.token);
        }
        
        // Store user data as a simple JSON string
        const userDataStr = JSON.stringify({
          user_id: data.user_id || "",
          email: data.email || "",
          name: data.name || "",
          user_type: data.user_type || "participant",
          picture: data.picture || null,
          profile_completed: Boolean(data.profile_completed)
        });
        localStorage.setItem("pending_user_data", userDataStr);

        setStatus("success");
        setMessage(`Welcome! Redirecting...`);
        
        // Routing logic:
        // - Authenticated worker without a completed profile → resume the worker-signup wizard
        //   starting at the personal-info step (skipping Welcome + Account).
        // - Participant without a completed profile → /onboarding (which now starts with the
        //   funding step for Google signups missing NDIS info).
        // - Otherwise → role-appropriate dashboard / pending screen.
        let dest;
        if (!data.profile_completed) {
          dest = data.user_type === "support_worker"
            ? "/worker-signup?continue=1"
            : "/onboarding";
        } else if (data.user_type === "support_worker") {
          dest = (data.approval_status && data.approval_status !== "approved")
            ? "/worker-pending"
            : "/worker-dashboard";
        } else {
          dest = "/dashboard";
        }
        setTimeout(() => { window.location.href = dest; }, 500);
        
      } catch (err) {
        console.error("Auth error:", err);
        setStatus("error");
        setMessage(err.message || "Authentication failed. Redirecting...");
        setTimeout(() => { window.location.href = "/login"; }, 1500);
      }
    };

    processCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#87CEEB]">
      <div className="text-center">
        <div 
          className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-white border-t-transparent"
          style={{ animation: "spin 1s linear infinite" }}
        />
        <p className="text-white font-medium text-lg">{message}</p>
        {status === "error" && (
          <p className="text-white/70 text-sm mt-2">
            If you're not redirected, <a href="/" className="underline">click here</a>
          </p>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
